import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import TeamworkGraphIcon from '@atlaskit/icon-lab/core/teamwork-graph';
import CrossIcon from '@atlaskit/icon/core/cross';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import PanelRightIcon from '@atlaskit/icon/core/panel-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	connectionsTwg: {
		id: 'byline-connections.menu.header.title-teamwork-graph-twg.do-not-translate',
		defaultMessage: 'Teamwork Graph',
		description:
			'Title shown at the top of the connections byline menu, which lists the links and references connected to the current page. "Teamwork Graph" is a product name and is intentionally not translated.',
	},
	infoButtonLabel: {
		id: 'byline-connections.menu.header.info-button.label',
		defaultMessage: 'About Teamwork Graph connections',
		description:
			'Accessible name of the information icon in the connections byline menu header. Hovering or focusing it explains how the connections stay up to date.',
	},
	infoButtonTooltip: {
		id: 'byline-connections.menu.header.info-button.tooltip',
		defaultMessage:
			"Links and references are automatically updated here as this page gets used across your team's work.",
		description:
			'Tooltip shown on hover over the information icon in the connections byline menu header, explaining that the connections keep themselves up to date.',
	},
	openPanelLabel: {
		id: 'byline-connections.menu.header.open-panel-button.label',
		defaultMessage: 'Open panel',
		description:
			'Accessible label and tooltip for the button in the connections byline menu header that opens the Teamwork Graph preview panel.',
	},
	closeLabel: {
		id: 'byline-connections.menu.header.close-button.label',
		defaultMessage: 'Close',
		description:
			'Accessible label and tooltip for the button in the connections byline menu header that closes the menu.',
	},
});

const styles = cssMap({
	header: {
		display: 'flex',
		width: '320px',
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.200'),
		flexDirection: 'column',
		justifyContent: 'center',
		alignItems: 'flex-start',
		gap: token('space.050'),
	},
	titleRow: {
		display: 'flex',
		width: '100%',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	actionGroup: {
		display: 'flex',
		justifyContent: 'flex-end',
		alignItems: 'center',
		gap: token('space.050'),
	},
});

export const HEADER_HEADING_ID = 'byline-connections-heading';

export type ConnectionsByLineMenuHeaderProps = {
	onOpenPreviewPanel: () => void;
	onClose: () => void;
};

export const ConnectionsByLineMenuHeader = ({
	onOpenPreviewPanel,
	onClose,
}: ConnectionsByLineMenuHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<Box xcss={styles.header}>
			<Box xcss={styles.titleRow}>
				<Inline space="space.100" alignBlock="center">
					<TeamworkGraphIcon label="" />
					<Heading id={HEADER_HEADING_ID} size="xsmall">
						{formatMessage(i18n.connectionsTwg)}
					</Heading>
				</Inline>
				<Box xcss={styles.actionGroup}>
					<IconButton
						spacing="default"
						appearance="subtle"
						label={formatMessage(i18n.infoButtonLabel)}
						icon={InformationCircleIcon}
						isTooltipDisabled={false}
						tooltip={{ content: formatMessage(i18n.infoButtonTooltip) }}
						testId="connections-byline-menu.info-button"
					/>
					<IconButton
						spacing="default"
						appearance="subtle"
						label={formatMessage(i18n.openPanelLabel)}
						icon={PanelRightIcon}
						onClick={onOpenPreviewPanel}
						testId="connections-byline-menu.open-panel-button"
					/>
					<IconButton
						spacing="default"
						appearance="subtle"
						label={formatMessage(i18n.closeLabel)}
						icon={CrossIcon}
						onClick={onClose}
						testId="connections-byline-menu.close-button"
					/>
				</Box>
			</Box>
		</Box>
	);
};
